import React, { useState, FormEvent, useEffect } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../config/api';

interface AuthProps {
  onAuth: (user: any) => void; // Replace `any` with a proper user type if available
  type?: string; // Optional type prop
}

const Auth: React.FC<AuthProps> = ({ onAuth }) => {
  const [email, setEmail] = useState<string>(''); // Typed as string
  const [password, setPassword] = useState<string>(''); // Typed as string
  const [serverMessage, setServerMessage] = useState<string>(''); // Typed as string
  const [isLoading, setIsLoading] = useState<boolean>(false); // Track loading state
  
  // Check for passcode in URL when component mounts
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const passcode = urlParams.get('passcode');
    
    if (passcode) {
      setIsLoading(true);
      authenticateWithPasscode(passcode);
    }
  }, []);
  
  // Function to authenticate with passcode
  const authenticateWithPasscode = async (passcode: string) => {
    try {
      const response = await axios.post(
        `${API_BASE_URL}/auth/customer`,
        { passcode },
        { withCredentials: true }
      );
            
      onAuth(response.data.user);
      setServerMessage('Logged in as customer successfully!');
      
      // Optionally remove passcode from URL for security
      const newUrl = window.location.pathname;
      window.history.replaceState({}, document.title, newUrl);
    } catch (error: any) {
      console.error('Authentication error:', error.response?.data || error.message);
      setServerMessage(error.response?.data?.message || 'Invalid passcode');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setServerMessage(''); // Clear any previous message before submitting
    setIsLoading(true);
    
    try {
      const response = await axios.post(
        `${API_BASE_URL}/auth/login`,
        { email, password },
        { withCredentials: true }
      );
      onAuth(response.data.user);
      setServerMessage(response.data.message || 'Logged in successfully!'); // Display success message
    } catch (error: any) {
      console.error('Authentication error:', error.response?.data || error.message);
      setServerMessage(error.response?.data?.message || 'An error occurred'); // Display error message
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="container mt-5" style={{ maxWidth: '400px' }}>
      <div className="text-center mb-3">
        <img className="img-fluid" style={{maxWidth: "200px"}} src="/logo.png" alt="Fluora Firmware Logo" />
      </div>
      
      {isLoading ? (
        <div className="text-center">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-2">Verifying credentials...</p>
        </div>
      ) : (
        <>
          <h2>Login</h2>
          <form onSubmit={handleSubmit}>
            <div className="mb-3">
              <label htmlFor="email" className="form-label">
                Email
              </label>
              <input
                type="email"
                className="form-control"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>
            <div className="mb-3">
              <label htmlFor="password" className="form-label">
                Password
              </label>
              <input
                type="password"
                className="form-control"
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </div>
            <button type="submit" className="btn btn-primary">
              Login
            </button>
          </form>
        </>
      )}
      
      {/* Display server message */}
      {serverMessage && (
        <div className="mt-3">
          <p
            className={`alert ${
              serverMessage.includes('success') ? 'alert-success' : 'alert-danger'
            }`}
          >
            {serverMessage}
          </p>
        </div>
      )}
    </div>
  );
};

export default Auth;